import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    ImageRun,
    ExternalHyperlink,
    InternalHyperlink,
    Bookmark,
    Header,
    Footer,
    PageNumber,
    HeadingLevel,
    AlignmentType,
    UnderlineType,
    TabStopType,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    VerticalAlign,
} from "docx";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";

const SENSOR_FIELD_PLAN_DATASET_TYPE = "sensor-field-plan";

const TOWER_TYPES_WITHOUT_LEGS = ["H-Frame", "Wood Monopole", "Steel Monopole"];
const CARDINAL_DIRECTION_NAME_TO_ABBREV = {
    NORTH: "N", NORTHEAST: "NE", EAST: "E", SOUTHEAST: "SE",
    SOUTH: "S", SOUTHWEST: "SW", WEST: "W", NORTHWEST: "NW",
};
const TOWER_TYPE_LABELS = {
    LATTICE: "Lattice",
    H_FRAME: "H-Frame",
    WOOD_MONOPOLE: "Wood Monopole",
    STEEL_MONOPOLE: "Steel Monopole",
};

const TOOLS = ["Compass app", "Zipties", "Inclinometer", "Cordless Drill or Impact Driver", "3/4” or 19mm socket*", "19/32” or 15mm socket*", "9/16” or 14mm socket*", "1/2” or 13mm socket and one wrench*", "3/8” or 10mm socket*", "Flathead screwdriver", "Climbing gear, lifting equipment, side by side (where applicable)"]

function normalizeCardinalDirections(dirs) {
    return (dirs ?? []).map((d) => CARDINAL_DIRECTION_NAME_TO_ABBREV[d] ?? d);
}

function normalizeTowerType(type) {
    if (!type) return null;
    return TOWER_TYPE_LABELS[type] ?? type;
}


function spacer() {
    return new Paragraph({ text: "" });
}

export async function generateSensorFieldPlanDocx(clientName, projectName) {
    const result = await calibrationService.getLatestDatasetViaSignedUrl(
        clientName, projectName, DEFAULT_GROUP_KEY, SENSOR_FIELD_PLAN_DATASET_TYPE
    );
    if (!result?.data) return;
    const dataset = result.data;
    const versionDttm = result.meta?.version_dttm;

    const { project_installation_details: project, sensor_installations: sensors } = dataset;

    const logoBuffer = await fetch("/linevision-logo.png").then((r) => r.arrayBuffer());
    const rs1Monopole = await fetch("/rs1-monopole.png").then((r) => r.arrayBuffer());
    const rs1Lattice = await fetch("/rs1-lattice.png").then((r) => r.arrayBuffer());

    const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
    const noCellBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

    const logoTable = new Table({
        width: { size: 9360, type: WidthType.DXA },
        borders: {
            top: noBorder, bottom: noBorder, left: noBorder, right: noBorder,
            insideHorizontal: noBorder, insideVertical: noBorder,
        },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 6160, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        borders: noCellBorders,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.LEFT,
                                children: [new ImageRun({ data: logoBuffer, transformation: { width: 120, height: 17 } })],
                            }),
                        ],
                    }),
                    new TableCell({
                        width: { size: 3200, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        borders: noCellBorders,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [new TextRun({ text: "LineVision Inc.", color: "0040C2", size: 22, bold: true })],
                            }),
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [new TextRun({ text: "529 Main Street, Suite 307", size: 18 })],
                            }),
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                spacing: { after: 240 },
                                children: [new TextRun({ text: "Boston, MA 02129 USA", size: 18 })],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });

    const pageHeader = new Header({ children: [logoTable] });

    const pageFooter = new Footer({
        children: [
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ text: "LineVision Proprietary & Confidential", italics: true, size: 16, color: "0d0d0d" })],
            }),
            new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({ children: [PageNumber.CURRENT], italics: true, size: 16, color: "0d0d0d" })],
            }),
        ],
    });

    const field = (label, value, size = 24) => new Paragraph({
        spacing: { after: 120 },
        children: [
            new TextRun({ text: `${label}: `, bold: true, size }),
            new TextRun({ text: value !== null && value !== undefined && value !== "" ? String(value) : "N/A", size }),
        ],
    });

    const fieldLink = (label, href, linkText, size = 24) => new Paragraph({
        spacing: { after: 120 },
        children: [
            new TextRun({ text: `${label}: `, bold: true, size }),
            new ExternalHyperlink({
                link: href,
                children: [new TextRun({
                    text: linkText,
                    size,
                    color: "0040C2",
                    underline: { type: UnderlineType.SINGLE },
                })],
            }),
        ],
    });

    const towerTypeCounts = sensors.reduce((acc, s) => {
        const normalized = normalizeTowerType(s.tower_type) ?? "Unknown";
        const label = (normalized === "Wood Monopole" || normalized === "Steel Monopole") ? "Monopole" : normalized;
        acc[label] = (acc[label] ?? 0) + 1;
        return acc;
    }, {});

    const bullet = (text, size = 24) => new Paragraph({
        spacing: { after: 120 },
        indent: { left: 360 },
        children: [new TextRun({ text: `•  ${text}`, size })],
    });

    const sectionHeading = (text, pageBreakBefore = false) => new Paragraph({
        heading: HeadingLevel.HEADING_2,
        pageBreakBefore,
        spacing: { before: 300, after: 160 },
        children: [new TextRun({ text, bold: false, size: 32, color: "000000" })],
    });

    const installationPlanSection = [
        new Paragraph({
            spacing: { before: 200, after: 50 },
            children: [new TextRun({ text: `${clientName} / ${projectName} — Field Plan`, size: 46, bold: true })],
        }),

        sectionHeading("Installation Overview"),
        new Paragraph({
            spacing: { after: 120 },
            children: [
                new TextRun({ text: "Proposed Installation Dates: ", bold: true, size: 22 }),
                new TextRun({ text: "--", size: 22, highlight: "yellow" }),
            ],
        }),
        field("Total Sensor Installations", `${sensors.length} total sensor${sensors.length !== 1 ? "s" : ""}`, 22),
        field("Structure types", Object.entries(towerTypeCounts).map(([type, count]) => `${count}x ${type}`).join(", "), 22),
        sectionHeading("Required Tools by Contractor"),
        new Paragraph({
            spacing: { after: 240 },
            children: [
                new TextRun({ text: "*The hand tools below are primarily intended for SAE sizing. Metric suggestions are best fits but it is advised to use SAE tools.", size: 20, italics: true}),
            ],
        }),
        ...TOOLS.map((tool) => bullet(tool, 22)),
        sectionHeading("LineVision Operations Support Contact"),
        new Paragraph({
            spacing: { after: 80 },
            children: [new TextRun({ text: "Natalie Wilson, Field Hardware Operations Associate", bold: true, size: 22 })],
        }),
        new Paragraph({
            spacing: { after: 80 },
            children: [
                new TextRun({ text: "Email", size: 20, highlight: "yellow" }),
            ],
        }),
        new Paragraph({
            spacing: { after: 240 },
            children: [
                new TextRun({ text: "Phone", size: 20, highlight: "yellow"}),
            ],
        }),
       
        new Paragraph({
            spacing: { after: 80 },
            children: [new TextRun({ text: "Additional Contacts", size: 20, highlight: "yellow" })],
        })
        ];

    const assemblyOverview = [
        new Paragraph({
            heading: HeadingLevel.HEADING_2,
            pageBreakBefore: true,
            spacing: { before: 300, after: 80 },
            children: [
                new Bookmark({
                    id: "assembly_overview",
                    children: [new TextRun({ text: "Assembly Overview", bold: false, size: 32, color: "000000" })],
                }),
            ],
        }),
        new Paragraph({children: [new TextRun({text: "The below images depict installation examples on a steel monopole and a lattice structure type. In the final installed position, the Camera Module will be aimed at the conductors of the specified span. The Weather Sensor mounting arm may be aimed perpendicular or parallel to the conductors following the headings allowed for that site. The Power Module is always aimed South and tilted at the specified PV angle. The Power Module must not be shaded by the other components", size: 18})]}),
        new Table({
            width: { size: 9360, type: WidthType.DXA },
            borders: {
                top: noBorder, bottom: noBorder, left: noBorder, right: noBorder,
                insideHorizontal: noBorder, insideVertical: noBorder,
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 4680, type: WidthType.DXA },
                            verticalAlign: VerticalAlign.CENTER,
                            borders: noCellBorders,
                            children: [
                                new Paragraph({
                                    spacing: { before: 120, after: 120 },
                                    children: [new ImageRun({ data: rs1Monopole, transformation: { width: 350, height: 350 } })],
                                }),
                            ],
                        }),
                        new TableCell({
                            width: { size: 4680, type: WidthType.DXA },
                            verticalAlign: VerticalAlign.CENTER,
                            borders: noCellBorders,
                            margins: { left: 400 },
                            children: [
                                new Paragraph({
                                    spacing: { before: 120, after: 120 },
                                    children: [new TextRun({ text: "The image to the left depicts an installation on a steel monopole structure type with a banding installation method. In this example, the Camera Module is aimed at the conductors and the weather sensor mounting arm is aimed perpendicular to the conductors on the span. The Power Module is aimed South and is not shaded by the Camera Module.", italics: true, size: 16, color: "666666" })],
                                }),
                            ],
                        }),
                    ],
                }),
                new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 4680, type: WidthType.DXA },
                            verticalAlign: VerticalAlign.CENTER,
                            borders: noCellBorders,
                            children: [
                                new Paragraph({
                                    spacing: { before: 120, after: 120 },
                                    children: [new ImageRun({ data: rs1Lattice, transformation: { width: 350, height: 350 } })],
                                }),
                            ],
                        }),
                        new TableCell({
                            width: { size: 4680, type: WidthType.DXA },
                            verticalAlign: VerticalAlign.CENTER,
                            borders: noCellBorders,
                            margins: { left: 400 },
                            children: [
                                new Paragraph({
                                    spacing: { before: 120, after: 120 },
                                    children: [new TextRun({ text: "The image to the left depicts an installation on a lattice structure type. In this example, the Camera Module is pointed at the conductors, the weather sensor mounting arm is aimed parallel to the conductors and is not in the camera's field of view. The Power Module is aimed South and is not shaded by the Camera Module.", italics: true, size: 16, color: "666666" })],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ]
    const tocSection = [
        new Paragraph({
            pageBreakBefore: true,
            spacing: { before: 480, after: 480 },
            children: [new TextRun({ text: "Table of Contents", size: 40, bold: true })],
        }),
        new Paragraph({
            spacing: { after: 200 },
            tabStops: [{ type: TabStopType.RIGHT, position: 9360 }],
            children: [
                new InternalHyperlink({
                    anchor: "assembly_overview",
                    children: [new TextRun({
                        text: "Assembly Overview",
                        size: 28,
                        color: "0040C2",
                        underline: { type: UnderlineType.SINGLE },
                    })],
                }),
                new TextRun({ text: "\t3", size: 28 }),
            ],
        }),
        ...sensors.map((sensor, i) => new Paragraph({
            spacing: { after: 200 },
            tabStops: [{ type: TabStopType.RIGHT, position: 9360 }],
            children: [
                new InternalHyperlink({
                    anchor: `sensor_${i}`,
                    children: [new TextRun({
                        text: `Structure ${sensor.start_tower}`,
                        size: 28,
                        color: "0040C2",
                        underline: { type: UnderlineType.SINGLE },
                    })],
                }),
                new TextRun({ text: `\t${4 + i}`, size: 28 }),
            ],
        })),
    ];

    const sensorSections = sensors.flatMap((sensor, i) => {
        const towerType = normalizeTowerType(sensor.tower_type);
        const mastHeadings = normalizeCardinalDirections(sensor.allowed_mast_headings).join(", ") || null;
        const mountingLeg = TOWER_TYPES_WITHOUT_LEGS.includes(towerType) ? "N/A" : sensor.mounting_tower_leg;
        const spanHeading = normalizeCardinalDirections([sensor.span_heading])[0] || null;

        return [
            new Paragraph({
                heading: HeadingLevel.HEADING_1,
                pageBreakBefore: true,
                spacing: { before: 1440, after: 0 },
                children: [
                    new Bookmark({
                        id: `sensor_${i}`,
                        children: [new TextRun({ text: sensor.start_tower, bold: true, size: 40 })],
                    }),
                ],
            }),
            spacer(), 
            field("Circuit Name", sensor.line_name, 28),
            field("Install Structure", sensor.start_tower, 28),
            ...(sensor.structure_latitude != null && sensor.structure_longitude != null
                ? [fieldLink("Location", `https://www.google.com/maps?q=${sensor.structure_latitude},${sensor.structure_longitude}`, `${sensor.structure_latitude}, ${sensor.structure_longitude}`, 28)]
                : [field("Location", null, 28)]),
            field("Structure Type", towerType, 28),
            field("Mounting Leg", mountingLeg, 28),
            field("Camera Aim",  `Aim ${spanHeading ?? "N/A"} to ${sensor.end_tower ?? "N/A"}`, 28),
            field("PV Angle", `${project.pv_angle} degree angle`, 28),
            field("Install Height above Ground", sensor.install_hag != null ? `${parseFloat(sensor.install_hag.toFixed(1))} m (${(Math.round(sensor.install_hag * 3.28084 * 2) / 2).toFixed(1)} ft)` : null, 28),
            new Paragraph({ spacing: { after: 120 }, indent: { left: 360 }, children: [new TextRun({ text: "This is the approximate final installed height for the weather sensor. The weather sensor is the highest installed component.", size: 20 })] }),
            field("Allowed Weather Sensor Headings", mastHeadings, 28),
            new Paragraph({ spacing: { after: 120 }, indent: { left: 360 }, children: [new TextRun({ text: "The weather sensor must be oriented either parallel or perpendicular to the conductors.", size: 20 })] }),


            
        ];
    });

    const doc = new Document({
        styles: {
            default: {
                document: {
                    run: { font: "Montserrat" },
                },
            },
        },
        sections: [
            {
                page: {
                    size: { width: 12240, height: 15840 },
                    margin: { top: 1440, right: 1440, bottom: 1440, left: 1440, header: 720, footer: 720 },
                },
                headers: { default: pageHeader },
                footers: { default: pageFooter },
                children: [
                    ...installationPlanSection,
                    ...tocSection,
                    ...assemblyOverview,
                    ...sensorSections,
                ],
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dttmStamp = versionDttm ? `_${versionDttm.replace(/\.\d+/, "").replace(/[:.]/g, "-").replace("Z", "")}` : "";
    a.download = `${clientName}_${projectName}_sensor-field-plan${dttmStamp}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

}
